import { triggerHapticFeedback } from "../../../lib/tg";
import {
  SHOWCASE_DESIGN_PARTS,
  showcaseDesignResourceOwner,
  toShowcaseDesignBooleanState,
  type ShowcaseDesignResourceState,
} from "../../../lib/showcaseDesignContract";
import type { PreCheckStatus, ShowcaseResource } from "../lib/types";

const showcaseIconModules = {
  ...import.meta.glob(
    "../../../assets/showcase-icons/*.{webp,png,jpg,jpeg,gif,svg}",
    {
      eager: true,
      import: "default",
    },
  ),
  ...import.meta.glob(
    "../../../assets/showcase-icons/*.{WEBP,PNG,JPG,JPEG,GIF,SVG}",
    {
      eager: true,
      import: "default",
    },
  ),
} as Record<string, string>;

const showcaseIcons = Object.entries(showcaseIconModules)
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([, src]) => src);

export function randomShowcaseIcon(): string | undefined {
  return showcaseIcons[Math.floor(Math.random() * showcaseIcons.length)];
}

type Props = {
  resource: ShowcaseResource;
  iconSrc?: string;
  status: PreCheckStatus;
  busy?: boolean;
  message?: {
    tone: "error" | "hint";
    text: string;
  };
  onClick: () => void;
  onVerify: () => void;
};

export function ResourceCard({
  resource,
  iconSrc: iconOverride,
  status,
  busy = false,
  message,
  onClick,
  onVerify,
}: Props) {
  if (status === "member" || status === "system_bot_revoked") return null;

  const title = resource.public_name?.trim() || resource.name;
  const description = resource.public_description?.trim();
  const isNoCheck = resource.verification_mode === "no_check";
  const checkAction = isNoCheck ? "open-and-complete" : "verify-membership";
  const verifyLabel = isNoCheck ? "открыть и проверить" : "проверить подписку";
  const iconSrc = iconOverride ?? iconForResource(resource, title);
  const owner = showcaseDesignResourceOwner(resource.is_driver_own);
  const designStatus: ShowcaseDesignResourceState = status;
  const handleCheckClick = () => {
    triggerHapticFeedback();
    if (isNoCheck) {
      onClick();
      return;
    }
    onVerify();
  };

  return (
    <div
      className="tp-showcase-resource-card"
      data-tp-part={SHOWCASE_DESIGN_PARTS.resourceCard}
      data-status={designStatus}
      data-busy={toShowcaseDesignBooleanState(busy)}
      data-resource-type={resource.type}
      data-verification-mode={resource.verification_mode}
      data-owner={owner}
      data-has-description={toShowcaseDesignBooleanState(Boolean(description))}
      data-has-icon={toShowcaseDesignBooleanState(Boolean(iconSrc))}
      aria-busy={busy}
    >
      <button
        type="button"
        className="tp-showcase-resource-main"
        data-tp-part={SHOWCASE_DESIGN_PARTS.resourceOpen}
        data-action="open-resource"
        disabled={busy}
        onClick={onClick}
        aria-label={`открыть ресурс ${title}`}
      >
        <span
          className="tp-showcase-resource-icon"
          data-tp-part={SHOWCASE_DESIGN_PARTS.resourceIcon}
          aria-hidden="true"
        >
          {iconSrc ? (
            <img
              src={iconSrc}
              alt=""
              className="tp-showcase-resource-icon-image"
              data-tp-part={SHOWCASE_DESIGN_PARTS.resourceIconImage}
              loading="lazy"
              decoding="async"
              width={48}
              height={48}
              draggable={false}
            />
          ) : null}
        </span>
        <span
          className="tp-showcase-resource-copy"
          data-tp-part={SHOWCASE_DESIGN_PARTS.resourceCopy}
        >
          <span
            className="tp-showcase-resource-title"
            data-tp-part={SHOWCASE_DESIGN_PARTS.resourceTitle}
          >
            {title}
          </span>
          {description ? (
            <span
              className="tp-showcase-resource-description"
              data-tp-part={SHOWCASE_DESIGN_PARTS.resourceDescription}
            >
              {description}
            </span>
          ) : null}
        </span>
      </button>
      <button
        type="button"
        className="tp-showcase-check-button"
        data-tp-part={SHOWCASE_DESIGN_PARTS.resourceCheck}
        data-action={checkAction}
        disabled={busy}
        onClick={handleCheckClick}
        aria-label={verifyLabel}
      >
        <CheckIcon />
      </button>
      {message ? (
        <div
          className={
            message.tone === "error"
              ? "tp-alert"
              : "text-sm text-muted-foreground"
          }
          data-resource-message-tone={message.tone}
          role={message.tone === "error" ? "alert" : "status"}
        >
          {message.text}
        </div>
      ) : null}
    </div>
  );
}

function iconForResource(
  resource: ShowcaseResource,
  title: string,
): string | undefined {
  if (showcaseIcons.length === 0) return undefined;
  const seed = `${resource.id}:${resource.type}:${title}`;
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }
  return showcaseIcons[hash % showcaseIcons.length];
}

function CheckIcon() {
  return (
    <svg
      className="tp-showcase-check-icon"
      data-tp-part={SHOWCASE_DESIGN_PARTS.resourceCheckIcon}
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
